/**
 * @file Observability Instrument Attributes
 *
 * This file defines the structured attribute system for Mews observability.
 * Attributes follow a domain-based hierarchy: [Scope].[Domain].[Subdomain].[Instrument Name]
 *
 * Key principles:
 * - All attributes use 'mews' scope for platform consistency
 * - Domains align with business areas and team ownership
 * - Subdomains provide granular categorization within domains
 * - Type-safe attribute definitions prevent naming inconsistencies
 *
 * More details: https://mews.atlassian.net/wiki/spaces/EKB/pages/896338999/Observability+Instruments+Guidance
 */
import { CoralogixLogSeverity, CustomMeasurementData } from '@coralogix/browser';

/**
 * Instrument scope - defines the platform context for all attributes.
 * Currently only 'mews' scope is used to ensure consistency across frontend and backend.
 *
 * @example
 * ```typescript
 * // All attributes start with mews scope
 * `${InstrumentScope.Mews}.user.id` // "mews.user.id"
 * `${InstrumentScope.Mews}.enterprise.id` // "mews.enterprise.id"
 * ```
 */
enum InstrumentScope {
    /** Global Mews platform scope - shared between frontend and backend */
    Mews = 'mews',
}

/**
 * Business domains that categorize telemetry data by ownership and functionality.
 * Each domain corresponds to a specific business area.
 *
 * Domains align with the business domain registry:
 * https://mews.atlassian.net/wiki/spaces/EKB/pages/871400061/Business+domain+registry
 *
 * @example
 * ```typescript
 * // User domain attributes
 * `mews.${InstrumentDomain.User}.identity.login_method`
 *
 * // Reservations domain attributes
 * `mews.${InstrumentDomain.Reservations}.spaces.room_type`
 *
 * // Billing domain attributes
 * `mews.${InstrumentDomain.Billing}.invoices.amount`
 * ```
 */
export enum InstrumentDomain {
    User = 'user',
    Pricing = 'pricing',
    Analytics = 'analytics',
    Connectivity = 'connectivity',
    Distribution = 'distribution',
    Integrations = 'integrations',
    Onboarding = 'onboarding',
    Subscriptions = 'subscriptions',
    Payouts = 'payouts',
    Risk = 'risk',
    FinancialServices = 'financial_services',
    Reservations = 'reservations',
    Jobs = 'jobs',
    Billing = 'billing',
    Accounting = 'accounting',
    DX = 'dx',
}

/**
 * Subdomains provide more granular categorization within each business domain.
 * They represent specific functional areas or services within the broader domain.
 *
 * @example
 * ```typescript
 * // User domain subdomains
 * `mews.user.${InstrumentSubdomain.Identity}.profile_updated`
 * `mews.user.${InstrumentSubdomain.Authn}.login_attempt`
 * `mews.user.${InstrumentSubdomain.Authz}.permission_check`
 *
 * // Reservations domain subdomains
 * `mews.reservations.${InstrumentSubdomain.Spaces}.room_assigned`
 * `mews.reservations.${InstrumentSubdomain.RoutingRules}.rule_applied`
 * ```
 */
export enum InstrumentSubdomain {
    Identity = 'identity',
    Authn = 'authn',
    Authz = 'authz',
    Onboarding = 'onboarding',
    BankAccounts = 'bank_accounts',
    Spaces = 'spaces',
    RoutingRules = 'routing_rules',
    Bills = 'bills',
    Invoices = 'invoices',
    Cashiers = 'cashiers',
    Ledgers = 'ledgers',
}

/**
 * Required namespaces for global entity identifiers.
 * These represent core business entities that must be tracked across all observability data.
 *
 * @example
 * ```typescript
 * // Global entity identifiers
 * `mews.${InstrumentRequiredNamespace.Enterprise}.id`
 * `mews.${InstrumentRequiredNamespace.Chain}.id`
 * `mews.${InstrumentRequiredNamespace.Profile}.id`
 * ```
 */
export enum InstrumentRequiredNamespace {
    Chain = 'chain',
    Domain = 'domain',
    Enterprise = 'enterprise',
    Portfolio = 'portfolio',
    Profile = 'profile',
}

/**
 * Optional namespaces for contextual information.
 * These provide additional context but are not required for all telemetry data.
 *
 * @example
 * ```typescript
 * // Contextual attributes
 * `mews.${InstrumentOptionalNamespace.User}.id`
 * `mews.${InstrumentOptionalNamespace.Route}` // Current page route
 * `mews.${InstrumentOptionalNamespace.Owner}` // Team ownership
 * `mews.${InstrumentOptionalNamespace.Deployment}.environment` // Deployment context
 * ```
 */
export enum InstrumentOptionalNamespace {
    Deployment = 'deployment',
    User = 'user',
    Owner = 'owner',
    Route = 'route',
}

/**
 * Required attributes for entity identification.
 * These are standard attributes that should be present for proper entity tracking.
 *
 * @example
 * ```typescript
 * // Entity identification
 * `mews.user.${InstrumentRequiredAttribute.Id}`
 * `mews.enterprise.${InstrumentRequiredAttribute.Name}`
 * ```
 */
export enum InstrumentRequiredAttribute {
    Id = 'id',
    Name = 'name',
}

/**
 * Internal trace type classification for span categorization.
 * Used by the observability framework to organize different types of telemetry data.
 *
 * @internal
 */
export enum InstrumentTraceType {
    Analytics = 'analytics',
    Log = 'log',
    ParentSpan = 'parent_span',
}

/**
 * Type-safe instrument attribute definitions.
 * Enforces the correct structure for all observability attributes.
 *
 * Supports both standalone domains and domains with subdomains:
 * - Standalone: `mews.analytics.conversion_rate`
 * - With subdomain: `mews.user.identity.profile_updated`
 *
 * @example
 * ```typescript
 * // Valid attribute types
 * const userAttribute: InstrumentAttribute = 'mews.user.identity.login_method';
 * const reservationAttribute: InstrumentAttribute = 'mews.reservations.spaces.room_type';
 * const analyticsAttribute: InstrumentAttribute = 'mews.analytics.page_views';
 * ```
 */
export type InstrumentAttribute =
    // All domains (standalone) - for general domain-level attributes
    | `${InstrumentScope.Mews}.${InstrumentDomain.User}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Pricing}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Analytics}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Connectivity}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Distribution}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Integrations}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Onboarding}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Subscriptions}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Payouts}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Risk}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.FinancialServices}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Reservations}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Jobs}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Billing}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Accounting}.${string}`
    // Internal DX domain
    | `${InstrumentScope.Mews}.${InstrumentDomain.DX}.${string}`
    // User domain with subdomains - for specific user-related functionality
    | `${InstrumentScope.Mews}.${InstrumentDomain.User}.${InstrumentSubdomain.Authn}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.User}.${InstrumentSubdomain.Authz}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.User}.${InstrumentSubdomain.Identity}.${string}`
    // Risk domain with subdomains - for risk management processes
    | `${InstrumentScope.Mews}.${InstrumentDomain.Risk}.${InstrumentSubdomain.Onboarding}.${string}`
    // FinancialServices domain with subdomains - for financial operations
    | `${InstrumentScope.Mews}.${InstrumentDomain.FinancialServices}.${InstrumentSubdomain.BankAccounts}.${string}`
    // Reservations domain with subdomains - for booking and space management
    | `${InstrumentScope.Mews}.${InstrumentDomain.Reservations}.${InstrumentSubdomain.Spaces}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Reservations}.${InstrumentSubdomain.RoutingRules}.${string}`
    // Billing domain with subdomains - for billing and payment processes
    | `${InstrumentScope.Mews}.${InstrumentDomain.Billing}.${InstrumentSubdomain.Bills}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Billing}.${InstrumentSubdomain.Invoices}.${string}`
    | `${InstrumentScope.Mews}.${InstrumentDomain.Billing}.${InstrumentSubdomain.Cashiers}.${string}`
    // Accounting domain with subdomains - for financial record keeping
    | `${InstrumentScope.Mews}.${InstrumentDomain.Accounting}.${InstrumentSubdomain.Ledgers}.${string}`;

/**
 * Type for log-specific attributes.
 * All log attributes must start with the mews scope.
 */
export type LogAttribute = `${InstrumentScope.Mews}.${string}`;

/**
 * Type for log message attributes.
 * Extends LogAttribute to include message-specific attributes.
 */
export type LogMessageAttribute = `${LogAttribute}.message`;

/**
 * Log message attribute: "mews.message"
 * Used to store the main log message content.
 *
 * @example
 * ```typescript
 * { [LogMessage]: 'Payment completed successfully' }
 * ```
 */
export const LogMessage = `${InstrumentScope.Mews}.message`;

/**
 * Log severity attribute: "mews.severity"
 * Used to categorize log entries by severity level.
 *
 * @example
 * ```typescript
 * { [LogSeverity]: CoralogixLogSeverity.Info }
 * { [LogSeverity]: CoralogixLogSeverity.Warn }
 * ```
 */
export const LogSeverity = `${InstrumentScope.Mews}.severity`;

/**
 * Global chain identifier: "mews.chain.id"
 * Identifies the hotel chain context for multi-chain enterprises.
 *
 * @example
 * ```typescript
 * { [MewsChainId]: 'chain_12345' }
 * ```
 */
export const MewsChainId = `${InstrumentScope.Mews}.${InstrumentRequiredNamespace.Chain}.${InstrumentRequiredAttribute.Id}`;

/**
 * Global enterprise identifier: "mews.enterprise.id"
 * Identifies the specific property/enterprise context.
 *
 * @example
 * ```typescript
 * { [MewsEnterpriseId]: 'bb167a16-b678-4cc3-928c-aa8000f9d998' }
 * ```
 */
export const MewsEnterpriseId = `${InstrumentScope.Mews}.${InstrumentRequiredNamespace.Enterprise}.${InstrumentRequiredAttribute.Id}`;

/**
 * Global portfolio identifier: "mews.portfolio.id"
 * Identifies the portfolio grouping for multi-property management.
 *
 * @example
 * ```typescript
 * { [MewsPortfolioId]: 'portfolio_xyz789' }
 * ```
 */
export const MewsPortfolioId = `${InstrumentScope.Mews}.${InstrumentRequiredNamespace.Portfolio}.${InstrumentRequiredAttribute.Id}`;

/**
 * Global profile identifier: "mews.profile.id"
 * Identifies the user's profile context within the enterprise.
 *
 * @example
 * ```typescript
 * { [MewsProfileId]: 'profile_abc123' }
 * ```
 */
export const MewsProfileId = `${InstrumentScope.Mews}.${InstrumentRequiredNamespace.Profile}.${InstrumentRequiredAttribute.Id}`;

/**
 * Global user identifier: "mews.user.id"
 * Identifies the current authenticated user.
 *
 * @example
 * ```typescript
 * { [MewsUserId]: 'user_xyz789' }
 * ```
 */
export const MewsUserId = `${InstrumentScope.Mews}.${InstrumentOptionalNamespace.User}.${InstrumentRequiredAttribute.Id}`;

/**
 * Page route attribute: "mews.route"
 * Identifies the current page or application route.
 *
 * @example
 * ```typescript
 * { [PageRoute]: '/reservations/create' }
 * ```
 */
export const PageRoute = `${InstrumentScope.Mews}.${InstrumentOptionalNamespace.Route}`;

/**
 * Page owner attribute: "mews.owner"
 * Identifies the team or component responsible for the current page.
 *
 * @example
 * ```typescript
 * { [PageOwner]: 'reservations' }
 * { [PageOwner]: 'billing' }
 * ```
 */
export const PageOwner = `${InstrumentScope.Mews}.${InstrumentOptionalNamespace.Owner}`;

/**
 * Deployment environment attribute: "mews.deployment.environment"
 * Identifies the deployment environment (development, staging, production).
 *
 * @example
 * ```typescript
 * { [DeploymentEnvironment]: 'prod' }
 * { [DeploymentEnvironment]: 'dev' }
 * ```
 */
export const DeploymentEnvironment = `${InstrumentScope.Mews}.${InstrumentOptionalNamespace.Deployment}.environment`;

/**
 * Trace type attribute: "mews.trace_type"
 * Internal attribute used to categorize different types of spans.
 *
 * @internal
 * @example
 * ```typescript
 * { [TraceType]: InstrumentTraceType.Analytics }
 * { [TraceType]: InstrumentTraceType.Log }
 * ```
 */
export const TraceType = `${InstrumentScope.Mews}.trace_type`;

/**
 * Parent span identifier used for creating the root span.
 * @internal
 */
export const ParentSpan = 'parent_span';

export type SpanAttributes = {
    [key in InstrumentAttribute]: string | number | boolean;
};

export type LogAttributes = {
    [key in LogMessageAttribute]: string;
} & {
    [key in LogAttribute]?: string;
};

export type AnalyticsAttributes = {
    [key in InstrumentAttribute]: string | number | boolean;
};

export type MetricAttributes = Record<InstrumentAttribute, CustomMeasurementData>;

export enum LogLevel {
    Warn = CoralogixLogSeverity.Warn,
    Info = CoralogixLogSeverity.Info,
    Error = CoralogixLogSeverity.Error,
}
